import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  Code2,
  ExternalLink,
  PlusCircle,
  RefreshCw,
} from "lucide-react";
import {
  SiCodechef,
  SiCodeforces,
  SiGeeksforgeeks,
  SiHackerrank,
  SiLeetcode,
} from "react-icons/si";
import { Card } from "@/common/components/ui/card";
import { Button } from "@/common/components/ui/button";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/common/components/ui/accordion";
import { cn } from "@/lib/utils.js";
import { PageHeader } from "@/common/components/shared/PageHeader";

export const PLATFORM_META = {
  leetcode: {
    name: "LeetCode",
    slug: "leetcode",
    icon: SiLeetcode,
    iconClass: "text-amber-500",
    iconWrap: "bg-amber-50",
    accent: "bg-emerald-800",
    profileUrl: (username) => `https://leetcode.com/u/${username}`,
  },
  codeforces: {
    name: "Codeforces",
    slug: "codeforces",
    icon: SiCodeforces,
    iconClass: "text-blue-600",
    iconWrap: "bg-blue-50",
    accent: "bg-emerald-800",
    profileUrl: (username) => `https://codeforces.com/profile/${username}`,
  },
  geeksforgeeks: {
    name: "GeeksforGeeks",
    slug: "geeksforgeeks",
    icon: SiGeeksforgeeks,
    iconClass: "text-green-600",
    iconWrap: "bg-green-50",
    accent: "bg-emerald-800",
    profileUrl: (username) => `https://www.geeksforgeeks.org/user/${username}`,
  },
  codechef: {
    name: "CodeChef",
    slug: "codechef",
    icon: SiCodechef,
    iconClass: "text-orange-700",
    iconWrap: "bg-orange-50",
    accent: "bg-emerald-800",
    profileUrl: (username) => `https://www.codechef.com/users/${username}`,
  },
  hackerrank: {
    name: "HackerRank",
    slug: "hackerrank",
    icon: SiHackerrank,
    iconClass: "text-emerald-600",
    iconWrap: "bg-emerald-50",
    accent: "bg-emerald-800",
    profileUrl: (username) => `https://www.hackerrank.com/profile/${username}`,
  },
};

export const cardClass =
  "gap-0 rounded-xl border-slate-200/70 bg-white py-0 shadow-sm shadow-slate-200/40";

export const formatNumber = (value) => {
  const n = Number(value) || 0;
  return Number.isInteger(n)
    ? n.toLocaleString()
    : n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

export const getPlatformMeta = (slug) =>
  PLATFORM_META[slug?.toLowerCase?.()] || {
    name: slug,
    slug,
    icon: Code2,
    iconClass: "text-slate-500",
    iconWrap: "bg-slate-50",
    accent: "bg-emerald-800",
    profileUrl: () => null,
  };

export function PlatformPageShell({ children }) {
  return (
    <div className="mx-auto flex w-full max-w-screen-2xl flex-col gap-6 pb-8">
      {children}
    </div>
  );
}

export function PlatformPageHeader({
  title,
  subtitle,
  username,
  profileUrl,
  lastSynced,
  refreshing,
  onRefresh,
  breadcrumb = "Overall Report",
  icon,
}) {
  return (
    <PageHeader
      breadcrumb={breadcrumb}
      title={title}
      subtitle={subtitle}
      icon={icon ?? <Code2 className="w-5 h-5" />}
      className="border-b border-slate-200/70 pb-6"
      actions={
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {lastSynced ? (
            <p className="rounded-full bg-slate-100 px-3 py-1.5 text-xs text-slate-500">
              Updated {format(new Date(lastSynced), "d MMM, h:mm a")}
            </p>
          ) : null}
          <Button
            size="sm"
            variant="outline"
            className="rounded-lg border-slate-200 bg-white px-3 text-slate-700 shadow-xs hover:bg-slate-50"
            onClick={onRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={cn("size-3.5", refreshing && "animate-spin")} />
            {refreshing ? "Refreshing" : "Refresh"}
          </Button>
        </div>
      }
    >
      {username ? (
        <p className="mt-1 flex items-center gap-2 text-sm text-slate-600">
          <span>@{username}</span>
          {profileUrl ? (
            <a
              href={profileUrl}
              target="_blank"
              rel="noreferrer"
              className="text-slate-400 transition-colors hover:text-slate-700"
              aria-label="Open profile"
            >
              <ExternalLink className="size-3.5" />
            </a>
          ) : null}
        </p>
      ) : null}
    </PageHeader>
  );
}

export function VerifyBanner({ onVerify }) {
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 sm:flex sm:items-center sm:justify-between">
      <div>
        <p className="text-sm font-semibold text-amber-900">Profile not verified</p>
        <p className="mt-0.5 text-sm text-amber-800/80">
          Verify your profile to showcase your stats here.
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="mt-3 border-amber-300 bg-white text-amber-900 hover:bg-amber-100 sm:mt-0"
        onClick={onVerify}
      >
        Verify profile
      </Button>
    </div>
  );
}

export function EmptyConnectState({ platformName, onConnect }) {
  return (
    <div className="flex min-h-80 flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-slate-200 bg-slate-50/50 px-6 py-12 text-center">
      <PlusCircle className="size-12 text-emerald-600" />
      <div>
        <h2 className="text-lg font-semibold text-slate-900">
          {platformName} not connected
        </h2>
        <p className="mt-1 max-w-md text-sm text-slate-500">
          Connect and verify your {platformName} profile to see your stats here.
        </p>
      </div>
      <Button size="sm" onClick={onConnect}>
        Connect profile
      </Button>
    </div>
  );
}

export function StatTilesRow({ items }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <div
            key={item.label}
            className={cn(
              "flex items-center gap-4 rounded-xl border px-4 py-4 shadow-sm shadow-slate-200/30",
              item.wrap
            )}
          >
            {Icon ? (
              <div
                className={cn(
                  "flex size-10 shrink-0 items-center justify-center rounded-xl",
                  item.iconWrap
                )}
              >
                <Icon className="size-5" />
              </div>
            ) : null}
            <div className="min-w-0">
              <p className="text-xs font-medium text-slate-500">{item.label}</p>
              <p
                className={cn(
                  "mt-0.5 text-2xl font-bold tabular-nums tracking-tight",
                  item.valueClass || "text-slate-950"
                )}
              >
                {formatNumber(item.value)}
              </p>
              {item.hint ? (
                <p className="truncate text-xs text-slate-500">{item.hint}</p>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function PlatformInternalScoreCard({ slug, scoreBreakdown }) {
  if (!scoreBreakdown) return null;
  const meta = getPlatformMeta(slug);
  const Icon = meta.icon;
  const score = scoreBreakdown.internalScore ?? scoreBreakdown.score ?? 0;

  return (
    <Card className={cn(cardClass, "overflow-hidden")}>
      <div className={cn("relative overflow-hidden px-5 py-5 text-white sm:px-6", meta.accent)}>
        <div className="pointer-events-none absolute -right-10 -top-16 size-40 rounded-full bg-white/10 blur-2xl" />
        <div className="relative flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-white/80">
              <Icon className="size-4" />
              <p className="text-xs font-semibold uppercase tracking-[0.16em]">
                Platform score
              </p>
            </div>
            <p className="mt-2 text-4xl font-bold tabular-nums tracking-tight sm:text-5xl">
              {formatNumber(score)}
            </p>
            <p className="mt-1 max-w-lg text-sm leading-6 text-white/75">
              Combined activity score for {meta.name}. This is not your raw problem count.
            </p>
          </div>
        </div>
      </div>

      {scoreBreakdown.formula ? (
        <Accordion type="single" collapsible className="px-3 sm:px-4">
          <AccordionItem value="how" className="border-0">
            <AccordionTrigger className="rounded-lg px-2 py-3.5 text-sm font-semibold text-slate-700 hover:no-underline hover:text-emerald-700">
              How is this score calculated?
            </AccordionTrigger>
            <AccordionContent className="px-2 pb-4">
              <p className="rounded-lg bg-emerald-50/70 px-3 py-2.5 font-mono text-xs leading-5 text-slate-600">
                {scoreBreakdown.formula}
              </p>
              {scoreBreakdown.parts?.length ? (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {scoreBreakdown.parts.map((part) => (
                    <span
                      key={part.label}
                      className="rounded-md border border-slate-100 bg-white px-2 py-1 text-xs text-slate-600"
                    >
                      {part.label} {part.detail} ={" "}
                      <span className="font-medium tabular-nums text-slate-800">
                        {formatNumber(part.value)}
                      </span>
                    </span>
                  ))}
                </div>
              ) : null}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : null}
    </Card>
  );
}

export function SectionCard({ title, hint, children, className }) {
  return (
    <Card className={cn(cardClass, "p-5 sm:p-6", className)}>
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {hint ? <p className="text-xs text-slate-400">{hint}</p> : null}
      </div>
      {children}
    </Card>
  );
}

export function PlatformHeatMapWrapper({ children }) {
  return (
    <div className="**:data-[slot=card]:rounded-xl **:data-[slot=card]:border-slate-200/70 **:data-[slot=card]:shadow-sm **:data-[slot=card]:shadow-slate-200/40">
      {children}
    </div>
  );
}

export function connectProfilePath() {
  return "/home/edit/profile/otherprofiles";
}

export function buildLeetcodeBreakdown(data = {}) {
  const easy = data["Easy count"] || 0;
  const medium = data["Medium count"] || 0;
  const hard = data["Hard count"] || 0;
  const rating = data.rating || 0;
  const contests = data.contestsAttended || data.contestParticipations?.length || 0;
  const internalScore =
    data.internalScore ??
    Math.floor(easy * 2 + medium * 4 + hard * 8 + rating + contests * 6);

  return {
    internalScore,
    formula: "(Easy × 2) + (Medium × 4) + (Hard × 8) + Rating + (Contests × 6)",
    parts: [
      { label: "Easy", detail: `${easy} × 2`, value: easy * 2 },
      { label: "Medium", detail: `${medium} × 4`, value: medium * 4 },
      { label: "Hard", detail: `${hard} × 8`, value: hard * 8 },
      { label: "Rating", detail: `${formatNumber(rating)}`, value: rating },
      { label: "Contests", detail: `${contests} × 6`, value: contests * 6 },
    ],
  };
}

export function buildGfgBreakdown(data = {}) {
  const stats = data.user_gfg_stats || {};
  const potd = data.extra_data?.currentPOTDStreak || 0;
  const codingScore = stats.coding_score || 0;
  const rating = stats.contest_rating || 0;
  const contests = stats.contests_attended || 0;
  const internalScore =
    stats.internalScore ??
    Math.floor(codingScore + rating + potd * 5 + contests * 6);

  return {
    internalScore,
    formula: "Coding Score + Rating + (POTD × 5) + (Contests × 6)",
    parts: [
      { label: "Coding score", detail: `${codingScore}`, value: codingScore },
      { label: "Rating", detail: `${formatNumber(rating)}`, value: rating },
      { label: "POTD streak", detail: `${potd} × 5`, value: potd * 5 },
      { label: "Contests", detail: `${contests} × 6`, value: contests * 6 },
    ],
  };
}

export function buildContestPlatformBreakdown(data = {}, type = "codeforces") {
  const problems =
    type === "codechef" ? data.totalProblemsSolved || 0 : data.totalSolved || 0;
  const rating =
    type === "codechef" ? data.rating || 0 : data.currentRating || 0;
  const contests =
    type === "codechef"
      ? data.contestsParticipated || 0
      : data.contestsAttended || 0;
  const internalScore =
    data.internalScore ?? Math.floor(problems * 6 + rating + contests * 8);

  return {
    internalScore,
    formula: "(Problems × 6) + Rating + (Contests × 8)",
    parts: [
      { label: "Problems", detail: `${problems} × 6`, value: problems * 6 },
      { label: "Rating", detail: `${formatNumber(rating)}`, value: rating },
      { label: "Contests", detail: `${contests} × 8`, value: contests * 8 },
    ],
  };
}

export function buildHackerrankBreakdown(data = {}) {
  const ds = data.structures_score || 0;
  const algo = data.algorithms_score || 0;
  const maths = data.mathematics_score || 0;
  const internalScore = data.internalScore ?? data.total_score ?? ds + algo + maths;

  return {
    internalScore,
    formula: "DS + Algo + Maths",
    parts: [
      { label: "DS", detail: `${ds}`, value: ds },
      { label: "Algo", detail: `${algo}`, value: algo },
      { label: "Maths", detail: `${maths}`, value: maths },
    ],
  };
}

export function ConnectLinkButton() {
  return (
    <Button size="sm" asChild>
      <Link to={connectProfilePath()}>Connect profile</Link>
    </Button>
  );
}
